import React, { useState, useRef } from 'react';
import { uploadAPI } from '../../utils/api';
import Button from '../common/Button';
import { FaUpload, FaTimes, FaImage } from 'react-icons/fa';

const ImageUpload = ({ onUpload, onImageUploaded, existingImage, initialImage = null, multiple = false }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingImage || initialImage);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Support both prop names for backwards compatibility
  const handleUploadCallback = onUpload || onImageUploaded;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) {
      return;
    }

    if (!multiple && files.length > 1) {
      setError('Please select only one image');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      if (multiple) {
        const uploadedUrls = [];
        for (const file of files) {
          const formData = new FormData();
          formData.append('image', file);
          const response = await uploadAPI.image(formData);
          const imageUrl = response.data.data?.url || response.data.url;
          uploadedUrls.push(imageUrl);
        }
        if (handleUploadCallback) {
          handleUploadCallback(uploadedUrls);
        }
      } else {
        const formData = new FormData();
        formData.append('image', files[0]);
        const response = await uploadAPI.image(formData);
        const imageUrl = response.data.data?.url || response.data.url;
        setPreview(imageUrl);
        if (handleUploadCallback) {
          handleUploadCallback(imageUrl);
        }
      }
    } catch (err) {
      console.error('Image upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (handleUploadCallback) {
      handleUploadCallback(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      {preview && !multiple && (
        <div className="relative inline-block">
          <img
            src={preview}
            alt="Preview"
            className="w-full max-w-md h-48 object-cover rounded-lg shadow-soft"
          />
          <button
            onClick={handleRemove}
            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-smooth"
            aria-label="Remove image"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Upload Button */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <Button
          type="button"
          variant={preview ? 'outline' : 'primary'}
          disabled={uploading}
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
        >
          {uploading ? (
            'Uploading...'
          ) : (
            <>
              {preview ? <FaImage className="inline mr-2" /> : <FaUpload className="inline mr-2" />}
              {preview ? 'Change Image' : (multiple ? 'Upload Images' : 'Upload Image')}
            </>
          )}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 font-body">{error}</p>
      )}

      {/* Help Text */}
      <p className="text-sm text-gray-600 font-body">
        Supported formats: JPG, PNG, GIF. Max size: 5MB
      </p>
    </div>
  );
};

export default ImageUpload;
